import { DeckTankStyle, DeckTanksWrapper, TankPlanContainer, MainTanksWrapper } from './TankPlanViewer.styles';
import { useStowageStore } from 'src/context/StowageContext';
import TankContainer from './TankContainer';
import { TankSideEnum } from 'src/types/ViewModel/Vessel/TankPlan';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import TankWrapper from './TankWrapper';
import { useMemo } from 'react';
import { calculateLayoutDimensions, calculateTankPosition, normalizeTankDimensions } from 'src/utils/stowage/tankPlanGridUtils';
import { TankPosition } from 'src/types/ViewModel/Vessel/TankPosition';
import { TankDimensions } from 'src/types/ViewModel/Vessel/TankDimensions';
import { getAllTanks } from 'src/utils/stowage/tankPlanUtils';
import { isDeckTank } from 'src/utils/stowage/tankUtils';

// Use PascalCase for interfaces
interface TankPlanViewProps {
    tankPlanCondition: TankPlanCondition;
    isReport?: boolean;
    filterTanksForReport?: boolean;
}

const TankPlanView: React.FC<TankPlanViewProps> = ({ tankPlanCondition, isReport = false, filterTanksForReport = false }) => {
    // Use camelCase for variables and destructured values
    const { tankPlan, useDynamicTankScaling, setZoomedTank, setMouseClickPosition } = useStowageStore();

    // Use SCREAMING_SNAKE_CASE for constants known prior to execution
    const TANKS_TO_LOG_COUNT = 20;

    // Use camelCase for constants initialized at runtime
    const enableLogging = false;

    // Add underscore prefix for private or utility functions
    const _logTankDimensions = (tankDimensions: TankDimensions[], prefix = 'Raw') => {
        if (!enableLogging) return;

        const tankDimensionsText = tankDimensions
            .slice(0, TANKS_TO_LOG_COUNT)
            .map((tankDimension) => {
                const rawData =
                    `startFrame:${tankDimension.startFrame.toString().padEnd(3)}, ` +
                    `endFrame:${tankDimension.endFrame.toString().padEnd(3)}, ` +
                    `yStart:${tankDimension.yStart.toString().padEnd(6)}, ` +
                    `width:${tankDimension.width.toString().padEnd(3)}, ` +
                    `height:${tankDimension.height.toString().padEnd(6)}`;

                return `Tank ${tankDimension.tank.odfjellTankName.padEnd(3)}: ${rawData}`;
            })
            .join('\n');
        console.log(`${prefix} tank positions (first ${TANKS_TO_LOG_COUNT}):\n` + tankDimensionsText);
    };

    const _logTankPositions = (positions: TankPosition[]) => {
        if (!enableLogging) return;

        const tankPositionsText = positions
            .slice(0, TANKS_TO_LOG_COUNT)
            .map((position) => {
                const calcData =
                    `x:${position.x.toString().padEnd(6)}, ` +
                    `y:${position.y.toString().padEnd(6)}, ` +
                    `width:${position.width.toString().padEnd(6)}, ` +
                    `height:${position.height.toString().padEnd(6)}`;

                return `Tank ${position.tank.odfjellTankName.padEnd(3)}: ${calcData}`;
            })
            .join('\n');
        console.log(`Calculated tank positions (first ${TANKS_TO_LOG_COUNT}):\n` + tankPositionsText);
    };

    // Use descriptive variable names and camelCase for handlers
    const handleZoomTank = (tank: any, event: React.MouseEvent) => {
        if (isReport) return; // Don't zoom in report mode

        const clickX = event.clientX;
        const clickY = event.clientY;
        setMouseClickPosition({ top: clickY, left: clickX });
        setZoomedTank(tank);
    };

    // Use useMemo for computationally expensive operations
    const tankPositions = useMemo(() => {
        if (!tankPlan) return [];

        const tanks = getAllTanks(tankPlan);
        const nonDeckTanks = tanks.filter((tank) => !isDeckTank(tank));

        // Calculate tank dimensions
        const tankDimensions = nonDeckTanks.map(
            (tank) =>
                ({
                    tank: tank,
                    startFrame: tank.startFrame,
                    endFrame: tank.endFrame,
                    yStart: tank.yStart,
                    width: tank.endFrame - tank.startFrame,
                    height: tank.height,
                } as TankDimensions)
        );

        // Normalize dimensions based on store settings
        const normalizedTankDimensions = normalizeTankDimensions(tankDimensions, useDynamicTankScaling);

        // Calculate layout and positions
        const layout = calculateLayoutDimensions(normalizedTankDimensions);
        const positions = normalizedTankDimensions
            .map((tankDimension) => calculateTankPosition(tankDimension, layout))
            .filter((item): item is TankPosition => item !== null)
            // Sort by y first, then x to ensure consistent rendering order
            .sort((positionA, positionB) => positionA.y - positionB.y || positionA.x - positionB.x);

        // Adjust tank widths to prevent gaps when dynamic scaling is disabled
        if (!useDynamicTankScaling) {
            // Process each tank individually rather than grouping
            for (const currentTank of positions) {
                // Find potential neighbor tanks to the right
                const potentialNeighbors = positions.filter(
                    (neighbor) =>
                        // Only consider tanks that are to the right of the current tank
                        neighbor.x > currentTank.x &&
                        // Check for vertical overlap - they must be in the same "horizontal line"
                        // We use a more precise check for overlap rather than exact matching
                        ((neighbor.y >= currentTank.y && neighbor.y < currentTank.y + currentTank.height) ||
                            (currentTank.y >= neighbor.y && currentTank.y < neighbor.y + neighbor.height))
                );

                if (potentialNeighbors.length > 0) {
                    // Find the closest neighbor to the right
                    const closestNeighbor = potentialNeighbors.reduce(
                        (closest, neighbor) => (neighbor.x < closest.x ? neighbor : closest),
                        potentialNeighbors[0]
                    );

                    // Calculate the gap between currentTank and its closest neighbor
                    const gap = closestNeighbor.x - (currentTank.x + currentTank.width);

                    // Only adjust width if there's a gap and the tanks
                    // have the same width and are at the same vertical level
                    if (
                        gap > 0 &&
                        Math.abs(currentTank.y - closestNeighbor.y) < 0.001 &&
                        Math.abs(currentTank.height - closestNeighbor.height) < 0.001
                    ) {
                        currentTank.width += gap;
                    }
                }
            }
        }

        // Log for debugging if needed
        _logTankDimensions(tankDimensions);
        _logTankDimensions(normalizedTankDimensions, 'Normalized');
        _logTankPositions(positions);

        return positions;
    }, [tankPlan, useDynamicTankScaling, enableLogging]);

    // Use descriptive function names
    const renderDeckTanks = (side: TankSideEnum) => {
        if (!tankPlan?.deckTanks || tankPlan.deckTanks.length === 0) {
            return <DeckTanksWrapper />;
        }

        const filteredDeckTanks = tankPlan.deckTanks.filter((deckTank) => deckTank.tank?.side === side);

        return (
            <DeckTanksWrapper>
                {filteredDeckTanks.map((deckTank) => (
                    <DeckTankStyle key={`deckTankStyle_${deckTank.tank.tankId}`} x={deckTank.xOffset} width={deckTank.width}>
                        <TankContainer
                            key={deckTank.tank.tankId}
                            tank={deckTank.tank}
                            onZoomClick={!isReport ? (e) => handleZoomTank(deckTank.tank, e) : undefined}
                            tankPlanCondition={tankPlanCondition}
                            isZoomed={false}
                            isReport={isReport}
                            filterTanksForReport={filterTanksForReport}
                        />
                    </DeckTankStyle>
                ))}
            </DeckTanksWrapper>
        );
    };

    if (!tankPlan) return null;

    const hasPortDeckTanks = tankPlan.deckTanks?.some((deckTank) => deckTank.tank?.side === TankSideEnum.Port);
    const hasStarboardDeckTanks = tankPlan.deckTanks?.some((deckTank) => deckTank.tank?.side === TankSideEnum.Starboard);

    return (
        <TankPlanContainer>
            {/* Port deck tanks at the top */}
            {hasPortDeckTanks && renderDeckTanks(TankSideEnum.Port)}

            {/* Main tanks in the middle */}
            <MainTanksWrapper $hasDeckTanks={hasPortDeckTanks || hasStarboardDeckTanks}>
                {tankPositions.map((position) => (
                    <TankWrapper
                        key={position.tank.tankId}
                        tank={position.tank}
                        x={position.x}
                        y={position.y}
                        width={position.width}
                        height={position.height}
                        onZoomClick={!isReport ? handleZoomTank : undefined}
                        tankPlanCondition={tankPlanCondition}
                        isReport={isReport}
                        filterTanksForReport={filterTanksForReport}
                    />
                ))}
            </MainTanksWrapper>

            {/* Starboard deck tanks at the bottom */}
            {hasStarboardDeckTanks && renderDeckTanks(TankSideEnum.Starboard)}
        </TankPlanContainer>
    );
};

export default TankPlanView;
